import { useState } from 'react';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import UploadModal from 'app/components/Uploads/UploadModal';
import UploadPreview from 'app/components/PhotoSwipe/UploadPreview';
import useAccessCodePageLogoUploadForm from 'app/hooks/useAccessCodePageLogoUploadForm';
import useAccessCodePageBackgroundUploadForm from 'app/hooks/useAccessCodePageBackgroundUploadForm';
import useAccessCodePageVideoUploadForm from 'app/hooks/useAccessCodePageVideoUploadForm';
import useAccessCodePageFaviconUploadForm from 'app/hooks/useAccessCodePageFaviconUploadForm';
import useAccessCodeMetaDataForm from 'app/hooks/useAccessCodeMetaDataForm';
import AccessCodePageForm from './AccessCodePageForm';
import LogoUploadProgressBar from './LogoUploadProgressBar';
import BackgroundUploadProgressBar from './BackgroundUploadProgressBar';
import VideoUploadProgressBar from './VideoUploadProgressBar';
import FaviconUploadProgressBar from './FaviconUploadProgressBar';

import {
  AND_OPERATOR,
} from 'app/hooks/useFilter';

import {
  cacheBustUrl
} from 'shared/utils/Utils';

import {
  getAccessCodePage
} from 'app/services/accessCodePages';
import AccessCodeMetaDataForm from './AccessCodeMetaDataForm';

const AccessCodePagePanel = props => {
  const {
    accessCodePage,
    action,
    onDelete,
    //onUploadStart,
    onClose,
    updateRow,
    onFormSubmit
  } = props;

  // setup logo, background, video & favicon Photoswipe items.
  // during upload they will be set to an UploadPreview component,
  // otherwise the src will be set
  const [logoImage, setLogoImage] = useState({
    src: cacheBustUrl(accessCodePage?.logo_url, Date.now())
  })

  const onLogoUploadStart = file => {
    uploadWorker.createObjectURL(file, url => {
      setLogoImage({
        jsx: <UploadPreview src={url} width={720} height={720} progressBar={<LogoUploadProgressBar accessCodePage={accessCodePage} />} />
      });
    });
  }

  const onLogoUploadComplete = async () => {
    let updatedPage = await getAccessCodePage(accessCodePage.id);
    updateRow(updatedPage);

    setLogoImage({
      src: cacheBustUrl(accessCodePage?.logo_url, Date.now())
    });
  }

  const [backgroundImage, setBackgroundImage] = useState({
    src: cacheBustUrl(accessCodePage?.background_url, Date.now())
  })

  const onBackgroundUploadStart = file => {
    uploadWorker.createObjectURL(file, url => {
      setLogoImage({
        jsx: <UploadPreview src={url} width={720} height={720} progressBar={<BackgroundUploadProgressBar accessCodePage={accessCodePage} />} />
      });
    });
  }

  const onBackgroundUploadComplete = async () => {
    let updatedPage = await getAccessCodePage(accessCodePage.id);
    updateRow(updatedPage);

    setBackgroundImage({
      src: cacheBustUrl(accessCodePage?.background_url, Date.now())
    });
  }

  const [videoImage, setVideoImage] = useState({
    src: cacheBustUrl(accessCodePage?.video_url, Date.now())
  })

  const onVideoUploadStart = file => {
    uploadWorker.createObjectURL(file, url => {
      setVideoImage({
        jsx: <UploadPreview src={url} width={720} height={720} progressBar={<VideoUploadProgressBar accessCodePage={accessCodePage} />} />
      });
    });
  }

  const onVideoUploadComplete = async () => {
    let updatedPage = await getAccessCodePage(accessCodePage.id);
    updateRow(updatedPage);

    setVideoImage({
      src: cacheBustUrl(accessCodePage?.video_url, Date.now())
    });
  }

  const [faviconImage, setFaviconImage] = useState({
    src: cacheBustUrl(accessCodePage?.favicon_url, Date.now())
  })

  const onFaviconUploadStart = file => {
    uploadWorker.createObjectURL(file, url => {
      setFaviconImage({
        jsx: <UploadPreview src={url} width={720} height={720} progressBar={<FaviconUploadProgressBar accessCodePage={accessCodePage} />} />
      });
    });
  }

  const onFaviconUploadComplete = async () => {
    let updatedPage = await getAccessCodePage(accessCodePage.id);
    updateRow(updatedPage);

    setLogoImage({
      src: cacheBustUrl(accessCodePage?.favicon_url, Date.now())
    });
  }

  const upsertMetadata = (action, metadata) => {
    onFormSubmit(metadata)
  }

  const {
    formIsOpen: logoFormIsOpen,
    onAdd: onLogoFormOpen,
    onFormClose: onLogoFormClose,
    onFormSubmit: onLogoFormSubmit
  } = useAccessCodePageLogoUploadForm({
    onUploadStart: onLogoUploadStart,
    onUploadComplete: onLogoUploadComplete,
    accessCodePage
  });

  const {
    formIsOpen: backgroundFormIsOpen,
    onAdd: onBackgroundFormOpen,
    onFormClose: onBackgroundFormClose,
    onFormSubmit: onBackgroundFormSubmit
  } = useAccessCodePageBackgroundUploadForm({
    onUploadStart: onBackgroundUploadStart,
    onUploadComplete: onBackgroundUploadComplete,
    accessCodePage
  });

  const {
    formIsOpen: videoFormIsOpen,
    onAdd: onVideoFormOpen,
    onFormClose: onVideoFormClose,
    onFormSubmit: onVideoFormSubmit
  } = useAccessCodePageVideoUploadForm({
    onUploadStart: onVideoUploadStart,
    onUploadComplete: onVideoUploadComplete,
    accessCodePage
  });

  const {
    formIsOpen: faviconFormIsOpen,
    onAdd: onFaviconFormOpen,
    onFormClose: onFaviconFormClose,
    onFormSubmit: onFaviconFormSubmit
  } = useAccessCodePageFaviconUploadForm({
    onUploadStart: onFaviconUploadStart,
    onUploadComplete: onFaviconUploadComplete,
    accessCodePage
  });

  const {
    formIsOpen: metaDataFormIsOpen,
    onEdit: onMetaDataFormOpen,
    onFormClose: onMetaDataFormClose,
    onFormSubmit: onMetaDataFormSubmit
  } = useAccessCodeMetaDataForm({ accessCodePage });

  // if we're creating a page then no actions panel will be shown.
  // ideally the user would be able to upload files as they create the page,
  // but currently an ID is required in the database and the page would need
  // to be pre-saved to enable this
  if (action === 'create') {
    return null;
  }


  let cards = [
    {
      label: 'Logo',
      image: {
        id: 'acp-logo',
        w: 400,
        h: 400,
        ...logoImage
      },
      actions: [
        { label: 'Upload New', callback: onLogoFormOpen }
      ]
    },
    {
      label: 'Background',
      image: {
        id: 'acp-background',
        w: 400,
        h: 400,
        ...backgroundImage
      },
      actions: [
        { label: 'Upload New', callback: onBackgroundFormOpen }
      ]
    },
    {
      label: 'Video',
      // todo: setup video upload preview
      video: {
        src: accessCodePage.video_url,
        type: accessCodePage.video_mime_type
      },
      actions: [
        { label: 'Upload New', callback: onVideoFormOpen }
      ]
    },
    {
      label: 'Favicon',
      image: {
        id: 'acp-favicon',
        w: 400,
        h: 400,
        ...faviconImage
      },
      actions: [
        { label: 'Upload New', callback: onFaviconFormOpen }
      ]
    },
    {
      label: 'Delete Page',
      message: 'Delete the access code page',
      actions: [
        { label: 'Delete Page', callback: onDelete }
      ]
    },
    {
      label: 'Page MetaData',
      message: 'Manage access code page metadata',
      actions: [
        { label: 'Update Metadata', callback: () => onMetaDataFormOpen(accessCodePage) }
      ]
    },
  ]

  return (
    <div>
      <ResourceModalPanel cards={cards} />
      <UploadModal
        isOpen={logoFormIsOpen}
        onClose={onLogoFormClose}
        onFormSubmit={onLogoFormSubmit}
        center={true}
      />
      <UploadModal
        isOpen={backgroundFormIsOpen}
        onClose={onBackgroundFormClose}
        onFormSubmit={onBackgroundFormSubmit}
        center={true}
      />
      <UploadModal
        isOpen={videoFormIsOpen}
        onClose={onVideoFormClose}
        onFormSubmit={onVideoFormSubmit}
        center={true}
      />
      <UploadModal
        isOpen={faviconFormIsOpen}
        onClose={onFaviconFormClose}
        onFormSubmit={onFaviconFormSubmit}
        center={true}
      />
      <AccessCodeMetaDataForm
        isOpen={metaDataFormIsOpen}
        onClose={onMetaDataFormClose}
        center={true}
        onFormSubmit={onMetaDataFormSubmit}
        accessCodePage={accessCodePage} />
    </div>
  );
}

const AccessCodePageModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    onDestroy,
    onUploadStart,
    accessCodePage,
    updateRow
  } = props;

  const action = accessCodePage ? 'update' : 'create';

  let title, subTitle = null;
  if (action === 'update') {
    title = `${accessCodePage.subdomain}.capturelife.com`;
    subTitle = accessCodePage.account_name;
  } else {
    title = 'New Page';
  }

  const onSubmit = params => onFormSubmit(action, params)
  const onDelete = () => onDestroy(accessCodePage.id);

  return (
    <div>
      <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
        <AccessCodePagePanel {...{ accessCodePage, action, onClose, onDelete, onUploadStart, updateRow, onFormSubmit }} />
        <AccessCodePageForm {...{ accessCodePage, action, onClose, onSubmit }} />
      </ResourceModal>
    </div>
  );
}

export default AccessCodePageModal;
